import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Plus, ArrowUpRight, ArrowDownLeft, RotateCcw, Clock, CheckCircle2, AlertTriangle, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api, { formatPaise, formatError } from '@/lib/api';
import TopUpDialog from '@/components/TopUpDialog';
import CouponRedeem from '@/components/CouponRedeem';
import AdPlanCard from '@/components/AdPlanCard';
import PricingExplainer from '@/components/PricingExplainer';
import TrustPanel from '@/components/TrustPanel';
import Pagination from '@/components/Pagination';

const typeConfig = {
  credit: { icon: ArrowUpRight, color: 'text-money', bg: 'bg-money-soft', label: 'Added to wallet' },
  promo: { icon: ArrowUpRight, color: 'text-money', bg: 'bg-money-soft', label: 'Coupon' },
  debit: { icon: ArrowDownLeft, color: 'text-rose-700 dark:text-rose-400', bg: 'bg-blush/60', label: 'Held for an ad' },
  refund: { icon: RotateCcw, color: 'text-money', bg: 'bg-money-soft', label: 'Returned to wallet' },
  withdrawal: { icon: Landmark, color: 'text-rose-700 dark:text-rose-400', bg: 'bg-blush/60', label: 'Refunded to you' },
  plan: { icon: ArrowDownLeft, color: 'text-rose-700 dark:text-rose-400', bg: 'bg-blush/60', label: 'Ads plan' },
};
const OUT = ['debit', 'withdrawal', 'plan'];
const rupees = (paise) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 10;

export default function WalletPage() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [razorpayActive, setRazorpayActive] = useState(false);
  const [refundPending, setRefundPending] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadTransactions = async (page) => {
    try {
      const { data } = await api.get('/api/wallet/transactions', { params: { page, page_size: PAGE_SIZE } });
      setTransactions(data.items); setTxTotal(data.total); setTxPage(page);
    } catch {}
  };
  // After money moves, show the balance and the newest entries again.
  const load = async () => {
    try {
      const { data } = await api.get('/api/wallet/balance');
      setBalance(data.balance_paise);
      setRazorpayActive(data.razorpay_active);
      setRefundPending(data.refund_pending);
    } catch {}
    await loadTransactions(1);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestRefund = async () => {
    if (!window.confirm(`Refund your available balance of ${formatPaise(balance)} to your original payment method?`)) return;
    try {
      await api.post('/api/wallet/refund-request');
      toast.success('Refund requested. It reaches your account within 5-7 working days after we process it.');
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;

  return (
    <div data-testid="wallet-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Wallet</h1>
        <p className="text-sm text-muted-foreground">Money for your ads and your ads plan. Your first ad is free.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="neo-card p-6 lg:col-span-3" data-testid="wallet-balance-card">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-xl bg-money-soft border-2 border-ink flex items-center justify-center">
              <Wallet className="w-5 h-5 text-money" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold">Available balance</span>
          </div>
          <p className="text-4xl sm:text-5xl font-extrabold font-heading tracking-tight text-money mb-5">{rupees(balance)}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button className="gradient-bg h-11 px-6" data-testid="top-up-wallet-button"
              disabled={!razorpayActive} onClick={() => setTopUpOpen(true)}>
              <Plus className="w-4 h-4" /> Add money
            </Button>
            {balance > 0 && !refundPending && (
              <Button variant="outline" onClick={requestRefund} data-testid="request-refund-btn">
                <Landmark className="w-4 h-4" /> Withdraw to bank
              </Button>
            )}
            {refundPending && <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Withdrawal in progress</span>}
          </div>
          <div className="mt-5 max-w-sm"><CouponRedeem onRedeemed={() => load()} /></div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            Money held for running ads isn&apos;t included above. Anything an ad doesn&apos;t spend comes back here.
            Coupon credit can be spent on ads but isn&apos;t withdrawable.
          </p>
        </motion.div>

        <PricingExplainer className="lg:col-span-2" />
      </div>

      <AdPlanCard onBought={() => load()} onNeedMoney={() => razorpayActive && setTopUpOpen(true)} />

      {!razorpayActive && (
        <div className="p-4 rounded-xl bg-amber-500/10 border-2 border-amber-500/40 flex items-start gap-3" data-testid="razorpay-pending">
          <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Payments aren&apos;t switched on yet</p>
            <p className="text-xs text-muted-foreground mt-1">Top-ups will open as soon as the payment keys are added.</p>
          </div>
        </div>
      )}

      <TrustPanel />

      <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} onPaid={() => load()} />

      <section aria-labelledby="history-title">
        <h2 id="history-title" className="text-lg font-extrabold mb-4 font-heading">Every rupee, in and out</h2>
        {transactions.length === 0 ? (
          <div className="text-center py-12 rounded-2xl bg-card border-2 border-dashed border-ink/40">
            <Clock className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          </div>
        ) : (
          <div className="neo-card divide-y-2 divide-ink/10 overflow-hidden">
            {transactions.map((tx) => {
              const config = typeConfig[tx.type] || typeConfig.credit;
              const Icon = config.icon;
              return (
                <div key={tx.id || tx._id} className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-9 h-9 rounded-lg ${config.bg} border-2 border-ink flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${config.color}`} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{tx.description || config.label}</p>
                      <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold font-mono ${config.color}`}>
                      {OUT.includes(tx.type) ? '−' : '+'}₹{(tx.amount_paise / 100).toFixed(2)}
                    </p>
                    <Badge variant="outline" className="text-[10px] mt-1">
                      {tx.status === 'completed' ? <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" /> : <Clock className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" />}
                      {tx.status}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={txPage} pageSize={PAGE_SIZE} total={txTotal} label="entries" onPage={loadTransactions} />
      </section>
    </div>
  );
}
